<template>
  <div>
    <div>
      <b-card-actions
        title="Filters"
        action-collapse
        collapsed
      >
        <b-row>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              label="Failed Date"
              label-for="v-ref-no"
            >
              <div class="position-relative">
                <AdvancedDateFilter
                  :date-value="filters['statuses.created_at']"
                  :opens="'right'"
                  :clear-icon="false"
                  @update="updateDateRange('statuses.created_at', $event)"
                />
              </div>
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              label="Rider"
              label-for="rider"
            >
              <v-select
                v-model="filters['staff.id']"
                :options="riderOptions"
                :reduce="option => option.id"
                label="first_name"
                placeholder="Select Rider"
              >
                <template v-slot:option="option">
                  {{ option.reference_id }} | {{ option.first_name }} {{ option.last_name }}
                </template>
              </v-select>
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              label="Warehouse"
              label-for="warehouse"
            >
              <v-select
                v-model="filters['app_warehouses.id']"
                :options="warehouseOptions"
                :reduce="option => option.id"
                label="name"
                placeholder="Select Warehouse"
              >
                <template v-slot:option="option">
                  {{ option.name }}
                </template>
              </v-select>
            </b-form-group>
          </b-col>
        </b-row>
        <!-- Apply and Clear Filters -->
        <b-row class="mt-1">
          <b-col>
            <filter-buttons
              :filter-data="filters"
              :apply-filters="applyFilters"
              :clear-filters="clearFilters"
            />
          </b-col>
        </b-row>
      </b-card-actions>
    </div>
    <div>
      <b-card
        no-body
      >
        <div
          class="m-2"
        >
          <b-row>
            <b-col
              class="d-flex align-items-center justify-content-end mb-1 mb-md-0"
            >
              <div>
                <ReportTableButtons
                  :items="items"
                  :json_fieldz="json_fields"
                  :name="`${downloadReportName} - Page ${page}.xls`"
                  :bulkname="`${downloadReportName}.xls`"
                  :fetch="fetchAssignOrderFailedToDeliverNoPagination"
                  @refresh="fetchAssignOrderFailedToDeliver"
                />
              </div>
            </b-col>
          </b-row>
        </div>
        <b-overlay
          id="overlay-background"
          blur="10px"
          opacity="0.30"
          rounded="sm"
          variant="light"
        >
          <b-table
            striped
            hover
            responsive
            :per-page="perPage"
            :items="items"
            :fields="fields"
            :json_fields="json_fields"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :sort-direction="sortDirection"
            show-empty
            sticky-header="100vh"
          >
            <template #empty>
              <TableDataFetching
                :rows="rows"
                :data-loading="dataLoading"
              />
            </template>
            <template #cell(failed_date)="data">
              {{ __dateTimeFormatter(data.value) }}
            </template>
            <template #cell(customer_name)="data">
              {{ getCustomerFieldValue(data.item, 'customer_name') }}
            </template>
            <template #cell(customer_email)="data">
              {{ getCustomerFieldValue(data.item, 'customer_email') }}
            </template>
            <template #cell(customer_address)="data">
              {{ getCustomerFieldValue(data.item, 'customer_address') }}
            </template>
            <template #cell(customer_phone)="data">
              <div>{{ getCustomerFieldValue(data.item, 'customer_phone') }}</div>
              <div
                v-if="shouldShowSecondaryPhone(data.item)"
                class="font-small-2"
              >
                {{ getCustomerFieldValue(data.item, 'customer_secondary_phone') || '-' }}
              </div>
            </template>
            <template #cell(reason)="data">
              {{ data.value || '-' }}
            </template>
            <template #cell(cod_value)="data">
              {{ __numberWithCommas(data.value, true) }}
            </template>
            <template #cell(rider)="data">
              <div class="d-flex align-items-center width-100">
                <div>
                  <div class="font-weight-bolder">
                    {{ data.item.rider ? data.item.rider : 'N/A' }}
                  </div>
                  <div class="font-small-2">
                    {{ data.item.rider_employee_id && data.item.rider_employee_id ? data.item.rider_employee_id : '' }}
                  </div>
                </div>
              </div>
            </template>
          </b-table>
        </b-overlay>
        <div class="mx-2 mb-2">
          <b-row
            cols="12"
          >
            <b-col
              md="3"
              class="d-flex align-items-center justify-content-sm-start"
            >
              <label class="width-75">Per page</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="pageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block"
              />
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              md="9"
              class="d-flex align-items-center justify-content-sm-end"
            >
              <span class="text-muted pagination-text  mt-1 mt-md-0 mr-1">Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} entries</span>
              <b-pagination
                v-model="page"
                :total-rows="(meta.total)? meta.total : 0"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>

            </b-col>
          </b-row>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BCol,
  BFormGroup,
  BRow,
  BTable,
  VBModal,
  BOverlay,
  BPagination,
} from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import Filter from '@core/mixins/filter'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import vSelect from 'vue-select'
import AdvancedDateFilter from '@/components/AdvancedDateFilter/AdvancedDateFilter.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import FilterButtons from '@/components/Filter/ReportFilterButtons.vue'
import getRiderList from '../AssignOrdersDeliveryRider/RiderListDropdown'
import getWarehouseList from '../ReturnOrderOriginWarehouse/WarehouseListDropdown'

const ReportRepository = RepositoryFactory.get('report')

export default {
  directives: {
    'b-modal': VBModal,
  },
  components: {
    BCardActions,
    vSelect,
    AdvancedDateFilter,
    BCard,
    BTable,
    BRow,
    BCol,
    BFormGroup,
    BOverlay,
    BPagination,
    FilterButtons,
  },
  mixins: [Filter, SuccessMessage, ErrorMessage],
  data() {
    return {
      riderOptions: [],
      warehouseOptions: [],
      filters: {
        'statuses.created_at': `${new Date(new Date().setHours(0, 0, 1, 0)).toISOString()} to ${new Date(new Date().setHours(23, 59, 59, 0)).toISOString()}`,
      },
      tableData: [],
      dataLoading: false,
      sort: '',
      perPage: 20,
      pageOptions: [10, 20, 50, 100, 150, 200],
      totalRows: 1,
      page: 1,
      meta: {},
      sortBy: '',
      sortDesc: false,
      sortDirection: 'asc',
      filter: null,
      filterOn: [],
      fields: [],
      json_fields: {
        'Failed Date': {
          field: 'failed_date',
          // eslint-disable-next-line no-underscore-dangle
          callback: value => this.__dateTimeFormatter(value),
        },
        'Waybill Number': 'waybill_number',
        Merchant: {
          field: 'merchant_name',
          callback: value => `"${value}"`,
        },
        'Customer Name': {
          field: 'customer_name',
          callback: value => `"${value}"`,
        },
        'Customer Email': {
          field: 'customer_email',
          callback: value => `"${value}"`,
        },
        'Customer Address': {
          field: 'customer_address',
          callback: value => `"${value}"`,
        },
        'Customer Phone': 'customer_phone',
        'Second Phone Number': 'customer_secondary_phone',
        COD: 'cod_value',
        Rider: {
          field: 'rider',
          callback: value => `"${value}"`,
        },
        'Rider Employee Id': {
          callback: value => `"${value.rider_employee_id !== null ? `${value.rider_employee_id}` : 'N/A'}"`,
        },
        Warehouse: {
          field: 'warehouse_name',
          callback: value => `"${value}"`,
        },
        Reason: {
          field: 'reason',
          callback: value => `"${value || ''}"`,
        },
      },
      currentDate: '',
      todayFilterQuery: '',
      downloadReportName: 'Assign-Order-Failed-To-Deliver',
      items: [],
    }
  },
  computed: {
    sortOptions() {
      return this.fields
        .filter(f => f.sortable)
        .map(f => ({ text: f.label, value: f.key }))
    },
  },
  watch: {
    options: {
      handler() {
        this.fetchAssignOrderFailedToDeliver()
      },
    },
    deep: true,
    page() {
      this.fetchAssignOrderFailedToDeliver()
    },
    perPage() {
      this.fetchAssignOrderFailedToDeliver()
    },
  },
  mounted() {
    this.fields.push(
      {
        key: 'failed_date',
        label: 'Failed Date',
        thClass: 'text-left',
        tdClass: 'text-left',
      },
      {
        key: 'waybill_number',
        label: 'Waybill Number',
        thClass: 'text-left',
        tdClass: 'text-left',
      },
      {
        key: 'merchant_name',
        label: 'Merchant',
        thClass: 'text-left',
        tdClass: 'text-left',
        sortable: true,
      },
      {
        key: 'customer_name',
        label: 'Customer Name',
        thClass: 'text-left',
        tdClass: 'text-left',
        sortable: true,
      },
      {
        key: 'customer_email',
        label: 'Customer Email',
        thClass: 'text-left',
        tdClass: 'text-left',
      },
      {
        key: 'customer_address',
        label: 'Customer Address',
        thClass: 'text-left',
        tdClass: 'text-left',
      },
      {
        key: 'customer_phone',
        label: 'Customer Phone',
        thClass: 'text-left',
        tdClass: 'text-left',
      },
      {
        key: 'rider',
        label: 'Rider',
        thClass: 'text-left',
        tdClass: 'text-left',
      },
      {
        key: 'warehouse_name',
        label: 'Warehouse',
        thClass: 'text-left',
        tdClass: 'text-left',
      },
      {
        key: 'reason',
        label: 'Reason',
        thClass: 'text-left',
        tdClass: 'text-left',
      },
      {
        key: 'cod_value',
        label: `COD (${this.currency_type})`,
        thClass: 'text-right',
        tdClass: 'text-right',
      },
    )
    this.totalRows = this.items.length
    this.setTodayFilter()
    this.fetchAssignOrderFailedToDeliver()
    this.riderList()
    this.warehouseList()
  },
  methods: {
    async fetchAssignOrderFailedToDeliver() {
      this.dataLoading = true
      this.items = []
      const filterQuery = this.buildFilterQuery()
      try {
        const { data } = (await ReportRepository.getAssignOrderFailedToDeliverReport(this.page, filterQuery, this.perPage))
        this.items = data.data
        this.meta = data.meta
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    async fetchAssignOrderFailedToDeliverNoPagination() {
      try {
        const filterQuery = this.buildFilterQuery()
        const { data } = (await ReportRepository.getAssignOrderFailedToDeliverReportNoPagiantion(filterQuery))
        return data.data
      } catch (e) {
        this.convertAndNotifyError(e)
        return []
      }
    },
    async riderList() {
      this.riderOptions = await getRiderList()
    },
    async warehouseList() {
      this.warehouseOptions = await getWarehouseList()
    },
    handleChangePage(page) {
      this.page = page
      this.fetchAssignOrderFailedToDeliver()
    },
    setTodayFilter() {
      const currentDate = this.todayFilter()
      this.updateDateRange('statuses.created_at', currentDate)
    },
    applyFilters() {
      this.fetchAssignOrderFailedToDeliver()
    },
    clearFilters() {
      this.filters = {
        'statuses.created_at': {
          startDate: null,
          endDate: null,
        },
      }
      this.setTodayFilter()
      this.$nextTick(() => {
        this.fetchAssignOrderFailedToDeliver()
      })
    },
    getCustomerFieldValue(item, fieldName) {
      if (!item) return ''
      if (item.is_encrypted) {
        if (this.$can('show-decrypted-orders')) {
          return item[fieldName] || ''
        }
        return '******'
      }
      return item[fieldName] || ''
    },
    shouldShowSecondaryPhone(item) {
      if (!item) return false
      const hasSecondaryPhone = item.customer_secondary_phone != null && item.customer_secondary_phone !== ''
      const isMaskingEncrypted = item.is_encrypted && !this.$can('show-decrypted-orders')
      return hasSecondaryPhone || isMaskingEncrypted
    },
  },
}
</script>
<style lang="scss" scoped>
@import '../../../@core/scss/vue/libs/vue-select';

.per-page-selector {
    width: 125px;
}
@media (min-width: 768px) and (max-width: 1023px) {
  .per-page-selector {
    width: 200px;
  }
}
</style>
